import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Fab, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "../../content/pages/homepage.module.css";
import { WidgetContext } from "src/contexts/WidgetContext";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LoaderFacts from "../Loader/Loader";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import PositionButtonsNews from "./PositionsButtonsNews";
import TextFieldNews from "../TextField/TextFieldNews";
import ButtonsScrollUp from "../ButtonsSections/ButtonScrollUp";
import SourceStories from "../SourceStories/SourceStories";
import SourceIcon from "../SourceStories/SourceIcon";

const NEWS_POSITIONS_ORDER = ["QB", "RB", "WR", "TE"];

function NewsResponse({ onClose, newsData, loadingNews }) {
  const [selectedPosition, setSelectedPosition] = useState("ALL");
  const [searchText, setSearchText] = useState("");
  const { selectedPartner, setSelectedPartner } = useContext(WidgetContext);
  const containerRef = useRef(null);
  const scrollableRef = useRef(null);
  const positions = ["ALL", "QB", "RB", "WR", "TE"];
  const currentPositionIndex = positions.indexOf(selectedPosition);

  //THIS IS LOGIC FOR THE TOUCH EVENTS IN MOBILE. FOR NOW ITS DISABLED, BUT WE CAN USE IT IN THE FUTURE (FOR THAT I DONT DELETE IT)
  // New states for the swipe
  // const [touchStart, setTouchStart] = useState(null);
  // const [touchEnd, setTouchEnd] = useState(null);
  // const minSwipeDistance = 50;

  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.between("xs", "lg"));

  const scrollToTop = () => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  //Handle position change for desktop and mobile
  const handlePositionChange = (e) => {
    if (e.target.closest("#scrollToTopNews") || e.target.closest("button") || e.target.closest("#searchNewsField")) {
      return;
    }

    if (!containerRef.current) {
      return;
    }

    const containerWidth = containerRef.current.clientWidth;
    const clickX = e.clientX - containerRef.current.getBoundingClientRect().left;

    if (clickX < containerWidth * 0.5 && currentPositionIndex > 0) {
      setSelectedPosition(positions[currentPositionIndex - 1]);
      scrollToTop();
    } else if (clickX > containerWidth * 0.5 && currentPositionIndex < positions.length - 1) {
      setSelectedPosition(positions[currentPositionIndex + 1]);
      scrollToTop();
    }
  };

  // Events for mobile/tablet touch
  // const onTouchStart = (e) => {
  //   if (e.target.closest("#scrollToTopNews")) {
  //     return;
  //   }
  //   setTouchEnd(null);
  //   setTouchStart(e.targetTouches[0].clientX);
  // };

  // const onTouchMove = (e) => {
  //   setTouchEnd(e.targetTouches[0].clientX);
  // };

  // const onTouchEnd = () => {
  //   if (!touchStart || !touchEnd) return;

  //   const distance = touchStart - touchEnd;
  //   const isLeftSwipe = distance > minSwipeDistance;
  //   const isRightSwipe = distance < -minSwipeDistance;

  //   if (isLeftSwipe && currentPositionIndex < positions.length - 1) {
  //     setSelectedPosition(positions[currentPositionIndex + 1]);
  //     scrollToTop();
  //   } else if (isRightSwipe && currentPositionIndex > 0) {
  //     setSelectedPosition(positions[currentPositionIndex - 1]);
  //     scrollToTop();
  //   }
  // };

  // Keep the useEffect for both desktop and mobile
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("click", handlePositionChange);
    return () => {
      container.removeEventListener("click", handlePositionChange);
    };
  }, [selectedPosition, currentPositionIndex]);

  // console.log("newsData", newsData);

  const goBack = () => {
    setSelectedPartner(null);
  };

  const handlePositionSelect = (positions) => {
    setSelectedPosition(positions);
  };

  const newsItems = useMemo(() => {
    if (!selectedPosition || !newsData?.news_by_position) {
      return [];
    }
    if (selectedPosition !== "ALL") {
      return newsData.news_by_position[selectedPosition] || [];
    }
    return NEWS_POSITIONS_ORDER.flatMap((pos) => newsData.news_by_position[pos] || []).sort(
      (a, b) => new Date(b?.summary?.lastNewsDate).getTime() - new Date(a?.summary?.lastNewsDate).getTime()
    );
  }, [newsData?.news_by_position, selectedPosition]);

  // Same highlight color as Faq.tsx / Glossary (soft salmon)
  // const HIGHLIGHT_BG = "#f99067";
  const HIGHLIGHT_BG = `${PureLightThemeCaaS.colors.alpha.orange[100]}`;


  const filteredNewsItems = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return newsItems || [];
    return (newsItems || []).filter((item) => {
      const nameMatch = (item.name || "").toLowerCase().includes(query);
      const summaryPlainText = (item.summary?.summary || "").replace(/<[^>]*>/g, "").toLowerCase();
      const summaryMatch = summaryPlainText.includes(query);
      const newsContent = (item.news?.content || "").toLowerCase();
      const newsTitle = (item.news?.title || "").toLowerCase();
      return nameMatch || summaryMatch || newsContent.includes(query) || newsTitle.includes(query);
    });
  }, [newsItems, searchText]);

  // Escape special regex chars (same pattern as Faq.tsx)
  const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Highlight search term in plain text (same logic as Faq.tsx highlightSearch)
  const highlightSearch = (text, search) => {
    if (!search?.trim()) return text;
    const lower = search.toLowerCase().trim();
    const parts = (text || "").split(new RegExp(`(${escapeRegex(search)})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === lower ? (
        <Box key={i} component="span" sx={{ bgcolor: HIGHLIGHT_BG, borderRadius: 0 }}>
          {part}
        </Box>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );
  };

  // Highlight search term in HTML or plain text (same color as Faq)
  const highlightInHtml = (html, search) => {
    if (!search?.trim() || !html) return html;
    const re = new RegExp(`(${escapeRegex(search)})`, "gi");
    const markStyle = `background-color:${HIGHLIGHT_BG};border-radius:0;padding:0;margin:0;font-weight:inherit`;
    const highlight = (s) => s.replace(re, `<mark style="${markStyle}">$1</mark>`);
    // Plain text (no tags): highlight entire string
    if (!html.includes("<") && !html.includes(">")) {
      return highlight(html);
    }
    // HTML: highlight only in text nodes
    return html
      .replace(/^([^<]*)</, (_, t) => highlight(t) + "<")
      .replace(/>([^<]*)</g, (_, t) => ">" + highlight(t) + "<")
      .replace(/>([^<]*)$/, (_, t) => ">" + highlight(t));
  };

  const addTargetBlankToLinks = (htmlString) => {
    if (!htmlString || !htmlString.includes("<a")) return htmlString;
    return htmlString.replace(/<a\b([^>]*)>/gi, (_, attrs) => {
      let next = attrs;
      if (!/\btarget=/i.test(next)) next += ' target="_blank"';
      if (!/\brel=/i.test(next)) next += ' rel="noopener noreferrer"';
      return `<a${next}>`;
    });
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <Box
        ref={containerRef}
        // onTouchStart={isMobileOrTablet ? onTouchStart : null}
        // onTouchMove={isMobileOrTablet ? onTouchMove : null}
        // onTouchEnd={isMobileOrTablet ? onTouchEnd : null}

        sx={{
          height: "100%",
          WebkitOverflowScrolling: "touch",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          "-ms-overflow-style": "none", // IE and Edge
          scrollbarWidth: "none",
        }}
      >
        {loadingNews ? (
          <LoaderFacts onClose={onClose} goBack={goBack} />
        ) : (
          <>
            <Box sx={{}} display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" p={1} pb={1}>
              <IconButton aria-label="close" onClick={onClose}>
                <ArrowBackIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }} />
              </IconButton>
              <Typography variant="h4" color={`${PureLightThemeCaaS.colors.alpha.white[100]}`} textAlign={"center"} p={1} className={styles.tradeTeam}>
                {selectedPartner} news
              </Typography>
              <IconButton aria-label="close" onClick={onClose}>
                <CloseIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }} />
              </IconButton>
            </Box>
            <>
              {/* Component for positions buttons */}
              <PositionButtonsNews onSelectPosition={handlePositionSelect} selectedPosition={selectedPosition} scrollToTop={scrollToTop} />
              {/* This box with id is necessary to avoid clicks on search field change positions button */}
              <Box id="searchNewsField">
                <TextFieldNews searchText={searchText} setSearchText={setSearchText} />
              </Box>
              <Box sx={{ border: "1px solid #e0e0e0", m: 1, mr: 2, ml: 2 }}></Box>
            </>
            <Box
              ref={scrollableRef}
              sx={{
                WebkitOverflowScrolling: "touch",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                "-ms-overflow-style": "none", // IE and Edge
                scrollbarWidth: "none",
                maxHeight: "auto",
                overflowY: "auto",
                p: 0.5,
                pb: 0,
                touchAction: "pan-y pinch-zoom", // allow vertical scroll
              }}
            >
              {filteredNewsItems?.map((player, index) => (
                <Box key={index} sx={{ m: 1, p: 1, borderRadius: 1 }}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mr: 1.5, ml: 1.5, p: 0.5, bgcolor: "#555555", borderRadius: 0.2 }}
                  >
                    <Typography variant="h6" color={`${PureLightThemeCaaS.colors.alpha.white[100]}`}>
                      {searchText.trim() ? highlightSearch(player.name, searchText.trim()) : player.name}
                    </Typography>
                    <Typography variant="h6" color={`${PureLightThemeCaaS.colors.alpha.white[100]}`}>
                      {new Date(player.summary.lastNewsDate).toLocaleDateString("en-US", {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                      })}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 0, pl: 2, pr: 2, pt: 3, pb: 0 }}>
                    <Typography variant="h6" sx={{ fontSize: "14px" }} color={`${PureLightThemeCaaS.colors.alpha.white[100]}`}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: addTargetBlankToLinks(
                            searchText.trim()
                              ? highlightInHtml(player.summary?.summary || "", searchText.trim())
                              : player.summary?.summary || ""
                          ),
                        }}
                      />
                    </Typography>
                    {/* <a href={player.news.url} target="_blank" rel="noopener noreferrer">
                    <Typography variant="h6" pt={1} color={`${PureLightThemeCaaS.colors.alpha.orange[100]}`}>
                      {player.news.source}
                    </Typography>
                  </a> */}
                  </Box>
                </Box>
              ))}
              {/* Hide SourceIcon when no results to avoid it floating up and being covered by header/search */}
              {filteredNewsItems?.length > 0 && (
                <Box sx={{ width: "100%", bgcolor: "#000000", mt: 0, mb: 0 }}>
                  <SourceIcon customPaddingBottom={2} customPaddingTop={0.5} />
                </Box>
              )}

            </Box>
          </>
        )}
      </Box>

      {/* Footer content */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "#000000",
          maxWidth: "1020px",
          margin: "0 auto",
          zIndex: 10,
        }}
      ></Box>
      {/* <SourceStories customPaddingBottom={1} customPaddingTop={1} /> */}

      <ButtonsScrollUp scrollToTop={scrollToTop} />
    </Box>
  );
}

export default NewsResponse;
